// src/components/SurveyFormBuilder.jsx - 커스텀 카드 레이아웃 설문 편집 컴포넌트

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faPaperPlane, 
  faEdit, 
  faTrash, 
  faGripVertical,
  faPlus,
  faChevronUp,
  faChevronDown,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import './SurveyFormBuilder.scss';

const SurveyFormBuilder = React.forwardRef(({ surveyData, onSave, onDistribute, hideHeader = false, isEditing = false }, ref) => {
  const [formData, setFormData] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [pendingAddQuestionBlockId, setPendingAddQuestionBlockId] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [editingOptionsIndex, setEditingOptionsIndex] = useState(null);
  const [editingOptions, setEditingOptions] = useState([]);

  // blocks(의미 단위) 기본값 + 하위호환
  const blocks = React.useMemo(() => {
    const fromData = surveyData?.blocks;
    if (Array.isArray(fromData) && fromData.length > 0) return fromData;
    return [
      { id: "intro", title: "Block 0: 안내/동의", kind: "intro", ai_comment: "조사 안내와 동의 확인입니다." },
      { id: "A_qualification", title: "Block A: 필수 자격 (Qualification)", kind: "qualification", ai_comment: "여기서 통과 못 하면 바로 종료됩니다." },
      { id: "B_demographics", title: "Block B: 배경 정보 (Demographics)", kind: "demographics", ai_comment: "나중에 쿼터(성비/연령비) 맞출 때 쓰이는 변수들입니다." },
      { id: "C_open_ended", title: "Block C: 심층 질문 (Open-ended)", kind: "open_ended", ai_comment: "인터뷰 대상자로 적합한지 판단할 때 읽어볼 내용입니다." },
      { id: "D_ops", title: "Block D: 운영 정보 (Ops)", kind: "ops", ai_comment: "운영에 필요한 정보입니다." },
    ];
  }, [surveyData?.blocks]);

  // surveyData에서 form_elements 또는 questions 추출 및 초기화
  useEffect(() => {
    if (!surveyData || isInitialized) return;

    console.log('SurveyFormBuilder - surveyData 확인:', surveyData);

    let elements = [];

    // 새로운 형식 (form_elements) 지원
    if (surveyData.form_elements && surveyData.form_elements.length > 0) {
      elements = surveyData.form_elements;
    }
    // 기존 형식 (questions) 지원 - 변환 필요
    else if (surveyData.questions && surveyData.questions.length > 0) {
      console.log('기존 questions 형식 감지, form_elements로 변환 중...');
      elements = surveyData.questions.map((q, idx) => {
        const baseElement = {
          id: q.id || `q${idx + 1}`,
          text: q.text || '',
          required: true,
        };

        // type에 따라 element 결정
        if (q.type && (q.type.includes('단일 선택') || q.type.includes('객관식'))) {
          baseElement.element = 'RadioButtons';
        } else if (q.type && (q.type.includes('다중 선택') || q.type.includes('다중선택'))) {
          baseElement.element = 'Checkboxes';
        } else if (q.type && q.type.includes('서술형')) {
          baseElement.element = 'TextArea';
        } else {
          baseElement.element = 'TextInput';
        }

        // options가 있으면 추가 (기존 형식에서는 options가 문자열 배열일 수 있음)
        if (q.options && Array.isArray(q.options)) {
          baseElement.options = q.options.map((opt, optIdx) => {
            if (typeof opt === 'string') {
              return { value: `opt${optIdx + 1}`, text: opt };
            }
            return opt; // 이미 {value, text} 형식인 경우
          });
        }

        return baseElement;
      });
    }

    if (elements.length > 0) {
      // react-form-builder2 형식으로 변환
      const convertedData = elements.map((element, idx) => {
        const baseItem = {
          id: element.id || `q${idx + 1}`,
          element: element.element,
          text: element.text,
          required: element.required !== false, // 기본값 true
          // 블록 단위 설계를 위한 메타데이터 (없어도 동작)
          block_id: element.block_id || element.section_id || undefined,
        };

        // RadioButtons 또는 Checkboxes인 경우 options 추가
        if ((element.element === 'RadioButtons' || element.element === 'Checkboxes') && element.options) {
          baseItem.options = element.options;
        }

        return baseItem;
      });

      setFormData(convertedData);
      setIsInitialized(true);
      console.log('SurveyFormBuilder 초기화 완료:', { surveyData, convertedData });
    } else {
      console.log('SurveyFormBuilder - form_elements 또는 questions가 없음:', surveyData);
    }
  }, [surveyData, isInitialized]);

  // 블록별 렌더링을 위한 표시 목록(순서: blocks, 내부: formData 순서 유지)
  const groupedForRender = React.useMemo(() => {
    const groups = (blocks || []).map((b) => ({
      block: b,
      items: [],
    }));
    const groupById = new Map(groups.map((g) => [g.block?.id, g]));
    const fallbackGroup =
      groupById.get((blocks?.[blocks.length - 1] || {}).id) ||
      groupById.get("D_ops") ||
      groups[groups.length - 1];

    (formData || []).forEach((item, actualIndex) => {
      const bid = item?.block_id || item?.section_id;
      const group = (bid && groupById.get(bid)) || fallbackGroup;
      if (group) group.items.push({ item, actualIndex });
    });

    return groups;
  }, [formData, blocks]);

  const flatRenderList = React.useMemo(() => {
    const out = [];
    groupedForRender.forEach((g) => {
      g.items.forEach((x) => out.push(x));
    });
    return out; // [{item, actualIndex}]
  }, [groupedForRender]);

  const findNeighborWithinSameBlock = (actualIndex, direction) => {
    // direction: -1 (up), +1 (down) within same block based on current render order
    const idx = flatRenderList.findIndex((x) => x.actualIndex === actualIndex);
    if (idx === -1) return null;
    const current = flatRenderList[idx];
    const currentBlockId = current?.item?.block_id;
    let j = idx + direction;
    while (j >= 0 && j < flatRenderList.length) {
      const cand = flatRenderList[j];
      if ((cand?.item?.block_id || null) === (currentBlockId || null)) return cand;
      // 블록이 달라지면 멈춤(블록 밖 이동은 금지)
      break;
    }
    return null;
  };

  // 문항 편집 시작
  const handleEditStart = (index) => {
    setEditingIndex(index);
    setEditingText(formData[index].text);
  };

  // 문항 편집 저장
  const handleEditSave = (index) => {
    const updated = [...formData];
    updated[index] = { ...updated[index], text: editingText };
    setFormData(updated);
    setEditingIndex(null);
    setEditingText('');
  };

  // 문항 편집 취소
  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditingText('');
  };

  // 문항 삭제
  const handleDelete = (index) => {
    if (window.confirm('이 문항을 삭제하시겠습니까?')) {
      const updated = formData.filter((_, i) => i !== index);
      setFormData(updated);
    }
  };

  // 문항 순서 변경 (위로)
  const handleMoveUp = (index) => {
    const neighbor = findNeighborWithinSameBlock(index, -1);
    if (!neighbor) return;
    const updated = [...formData];
    const j = neighbor.actualIndex;
    [updated[j], updated[index]] = [updated[index], updated[j]];
    setFormData(updated);
  };

  // 문항 순서 변경 (아래로)
  const handleMoveDown = (index) => {
    const neighbor = findNeighborWithinSameBlock(index, +1);
    if (!neighbor) return;
    const updated = [...formData];
    const j = neighbor.actualIndex;
    [updated[index], updated[j]] = [updated[j], updated[index]];
    setFormData(updated);
  };

  // 새 문항 추가 (타입 선택)
  const handleAddQuestion = (elementType = 'TextInput', blockId = null) => {
    const defaultBlockId =
      blockId ||
      pendingAddQuestionBlockId ||
      ((blocks && Array.isArray(blocks) && blocks.length > 0
        ? (blocks[blocks.length - 1] || {}).id
        : null) || 'D_ops');
    const newQuestion = {
      id: `q${Date.now()}`,
      element: elementType,
      text: '새 질문을 입력하세요',
      required: true,
      block_id: defaultBlockId,
      // RadioButtons나 Checkboxes인 경우 기본 옵션 추가
      ...(elementType === 'RadioButtons' || elementType === 'Checkboxes' 
        ? { options: [
            { value: 'opt1', text: '선택지 1' },
            { value: 'opt2', text: '선택지 2' }
          ]}
        : {}
      ),
    };
    setFormData([...formData, newQuestion]);
    setEditingIndex(formData.length);
    setEditingText('새 질문을 입력하세요');
    setShowAddQuestionModal(false);
    setPendingAddQuestionBlockId(null);
  };

  // 드래그 시작
  const handleDragStart = (e, index) => {
    // 버튼이나 입력 필드에서 드래그가 시작되는 것을 방지
    const target = e.target;
    const isInteractive = target.tagName === 'BUTTON' || 
                         target.tagName === 'INPUT' || 
                         target.tagName === 'TEXTAREA' ||
                         target.closest('button') !== null ||
                         target.closest('input') !== null ||
                         target.closest('textarea') !== null ||
                         target.closest('.question-actions') !== null ||
                         target.closest('.question-edit-actions') !== null ||
                         target.closest('.options-edit-actions') !== null ||
                         target.closest('.options-save-actions') !== null;
    
    if (isInteractive) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // 드래그 이미지 설정
    e.currentTarget.style.opacity = '0.5';
    e.currentTarget.style.cursor = 'grabbing';
  };

  // 드래그 종료
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    e.currentTarget.style.cursor = 'move';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // 드래그 오버
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    } else if (draggedIndex === index) {
      setDragOverIndex(null);
    }
  };

  // 드롭
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const updated = [...formData];
    const draggedItem = updated[draggedIndex];
    const dropItem = updated[dropIndex];
    const targetBlockId = dropItem?.block_id || dropItem?.section_id || draggedItem?.block_id;
    
    // 드래그된 항목 제거
    updated.splice(draggedIndex, 1);
    
    // 새로운 위치에 삽입
    // 위에서 아래로 드래그 (draggedIndex < dropIndex): 
    //   원래 위치를 제거하면 dropIndex가 하나씩 앞당겨지므로, dropIndex에 삽입하면 원래 dropIndex 위치 뒤에 들어감
    // 아래에서 위로 드래그 (draggedIndex > dropIndex):
    //   dropIndex 위치에 삽입
    const insertIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
    updated.splice(insertIndex, 0, {
      ...draggedItem,
      ...(targetBlockId ? { block_id: targetBlockId } : {}),
    });
    
    setFormData(updated);
    setDraggedIndex(null);
    setDragOverIndex(null);
    
    // 드롭 후 시각적 피드백 제거
    e.currentTarget.style.opacity = '1';
  };

  // 드래그 리브
  const handleDragLeave = (e) => {
    // 자식 요소로 이동하는 경우는 무시
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  // 선택지 편집 시작
  const handleEditOptionsStart = (index) => {
    const question = formData[index];
    if (question.options && question.options.length > 0) {
      setEditingOptions(question.options.map(opt => 
        typeof opt === 'string' ? { value: opt, text: opt } : opt
      ));
    } else {
      setEditingOptions([{ value: 'opt1', text: '' }, { value: 'opt2', text: '' }]);
    }
    setEditingOptionsIndex(index);
  };

  // 선택지 편집 저장
  const handleEditOptionsSave = (index) => {
    const updated = [...formData];
    // 빈 선택지 제거
    const validOptions = editingOptions.filter(opt => opt.text && opt.text.trim() !== '');
    updated[index] = { 
      ...updated[index], 
      options: validOptions.length > 0 ? validOptions : undefined
    };
    setFormData(updated);
    setEditingOptionsIndex(null);
    setEditingOptions([]);
  };

  // 선택지 편집 취소
  const handleEditOptionsCancel = () => {
    setEditingOptionsIndex(null);
    setEditingOptions([]);
  };

  // 선택지 추가
  const handleAddOption = () => {
    const newOption = { value: `opt${editingOptions.length + 1}`, text: '' };
    setEditingOptions([...editingOptions, newOption]);
  };

  // 선택지 삭제
  const handleDeleteOption = (optIndex) => {
    if (editingOptions.length > 1) {
      const updated = editingOptions.filter((_, i) => i !== optIndex);
      setEditingOptions(updated);
    }
  };

  // 선택지 텍스트 변경
  const handleOptionTextChange = (optIndex, value) => {
    const updated = [...editingOptions];
    updated[optIndex] = { ...updated[optIndex], text: value };
    setEditingOptions(updated);
  };

  // 저장 핸들러
  const handleSave = React.useCallback(() => {
    try {
      // 편집 중인 항목이 있으면 먼저 저장
      if (editingIndex !== null) {
        const updated = [...formData];
        updated[editingIndex] = { ...updated[editingIndex], text: editingText };
        setFormData(updated);
        setEditingIndex(null);
        setEditingText('');
      }

      // 데이터를 LLM JSON 형식으로 변환
      const llmJson = {
        form_elements: formData.map((item) => {
          const element = {
            id: item.id,
            element: item.element,
            text: item.text,
            required: item.required !== false,
            // 블록 단위 설계 메타데이터 (없어도 허용)
            ...(item.block_id ? { block_id: item.block_id } : {}),
          };

          // RadioButtons 또는 Checkboxes인 경우 options 포함
          if ((item.element === 'RadioButtons' || item.element === 'Checkboxes') && item.options) {
            element.options = item.options;
          }

          return element;
        }),
        target_groups: surveyData?.target_groups || [],
        key_variables: surveyData?.key_variables || [],
        balance_variables: surveyData?.balance_variables || [],
        // ✅ 기존 메타데이터 보존 (없어도 안전)
        blocks: surveyData?.blocks || [],
        screening_criteria: surveyData?.screening_criteria || [],
      };

      // 원본 형식으로 변환 (SURVEY_DATA 주석 포함)
      const content = `<!-- SURVEY_DATA\n${JSON.stringify(llmJson, null, 2)}\n-->\n\n# 스크리너 설문\n\n## 📝 설문 문항\n\n생성 완료`;

      onSave(content);
    } catch (error) {
      console.error('저장 오류:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  }, [formData, editingIndex, editingText, surveyData, onSave]);

  // 배포 핸들러
  const handleDistribute = React.useCallback(() => {
    try {
      const llmJson = {
        form_elements: formData.map((item) => {
          const element = {
            id: item.id,
            element: item.element,
            text: item.text,
            required: item.required !== false,
            ...(item.block_id ? { block_id: item.block_id } : {}),
          };

          if ((item.element === 'RadioButtons' || item.element === 'Checkboxes') && item.options) {
            element.options = item.options;
          }

          return element;
        }),
        target_groups: surveyData?.target_groups || [],
        key_variables: surveyData?.key_variables || [],
        balance_variables: surveyData?.balance_variables || [],
        blocks: surveyData?.blocks || [],
        screening_criteria: surveyData?.screening_criteria || [],
      };

      onDistribute({
        formData,
        llmJson,
      });
    } catch (error) {
      console.error('배포 준비 오류:', error);
      alert('배포 준비 중 오류가 발생했습니다.');
    }
  }, [formData, surveyData, onDistribute]);

  // 외부에서 호출할 수 있는 메서드 노출
  React.useImperativeHandle(ref, () => ({
    addQuestion: (elementType = 'TextInput', blockId = null) => {
      const defaultBlockId =
        blockId ||
        ((blocks && Array.isArray(blocks) && blocks.length > 0
          ? (blocks[blocks.length - 1] || {}).id
          : null) || 'D_ops');
      const newQuestion = {
        id: `q${Date.now()}`,
        element: elementType,
        text: '새 질문을 입력하세요',
        required: true,
        block_id: defaultBlockId,
        ...(elementType === 'RadioButtons' || elementType === 'Checkboxes' 
          ? { options: [
              { value: 'opt1', text: '선택지 1' },
              { value: 'opt2', text: '선택지 2' }
            ]}
          : {}
        ),
      };
      setFormData(prev => {
        const updated = [...prev, newQuestion];
        setEditingIndex(prev.length);
        setEditingText('새 질문을 입력하세요');
        return updated;
      });
    },
    save: () => handleSave(),
    distribute: () => handleDistribute(),
  }), [handleSave, handleDistribute]);

  // surveyData가 없거나 form_elements/questions가 없으면 로딩 표시
  const hasFormElements = surveyData?.form_elements && surveyData.form_elements.length > 0;
  const hasQuestions = surveyData?.questions && surveyData.questions.length > 0;
  
  if (!surveyData || (!hasFormElements && !hasQuestions)) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>설문 데이터를 불러오는 중...</p>
        {!surveyData && (
          <p style={{ color: '#999', fontSize: '14px', marginTop: '10px' }}>
            설문 데이터가 아직 생성되지 않았습니다.
          </p>
        )}
        {surveyData && !hasFormElements && !hasQuestions && (
          <p style={{ color: '#999', fontSize: '14px', marginTop: '10px' }}>
            설문 문항이 아직 생성되지 않았습니다. (form_elements 또는 questions가 비어있습니다)
          </p>
        )}
      </div>
    );
  }

  // element 타입을 한글로 변환
  const getElementTypeLabel = (element) => {
    const typeMap = {
      'RadioButtons': '단일 선택',
      'Checkboxes': '다중 선택',
      'TextArea': '서술형',
      'TextInput': '단답형',
    };
    return typeMap[element] || element;
  };

  // 문항 타입 옵션
  const questionTypes = [
    { value: 'TextInput', label: '단답형', icon: '📝' },
    { value: 'TextArea', label: '서술형', icon: '📄' },
    { value: 'RadioButtons', label: '단일 선택', icon: '🔘' },
    { value: 'Checkboxes', label: '다중 선택', icon: '☑️' },
  ];

  return (
    <div className="survey-form-builder-container">
      {!hideHeader && (
        <div className="survey-form-builder-header">
          <h3 className="survey-form-builder-title">설문 문항 편집</h3>
          <div className="survey-form-builder-header-actions">
            {isEditing && (
              <button
                className="btn-header-action"
                onClick={() => setShowAddQuestionModal(true)}
              >
                <FontAwesomeIcon icon={faPlus} />
                문항 추가
              </button>
            )}
            <button className="btn-header-action" onClick={handleSave}>
              <FontAwesomeIcon icon={faSave} />
              저장
            </button>
            {onDistribute && (
              <button className="btn-header-action" onClick={handleDistribute}>
                <FontAwesomeIcon icon={faPaperPlane} />
                배포
              </button>
            )}
          </div>
        </div>
      )}

      {showAddQuestionModal && (
        <div
          className="add-question-modal"
        >
          <div className="add-question-modal-content">
            <div className="modal-header">
              <h4>문항 타입 선택</h4>
              <button
                className="modal-close-btn"
                onClick={() => setShowAddQuestionModal(false)}
              >
                ×
              </button>
            </div>
            <div style={{ marginBottom: "14px" }}>
              <div style={{ fontWeight: 700, marginBottom: "8px" }}>추가할 블록</div>
              <select
                value={pendingAddQuestionBlockId || ""}
                onChange={(e) => setPendingAddQuestionBlockId(e.target.value || null)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  fontWeight: 700,
                }}
              >
                <option value="">(기본값: 마지막 블록)</option>
                {(blocks || []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.title || b.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="question-type-grid">
              {questionTypes.map((type) => (
                <button
                  key={type.value}
                  className="question-type-btn"
                  onClick={() => handleAddQuestion(type.value, pendingAddQuestionBlockId)}
                >
                  <span className="type-icon">{type.icon}</span>
                  <span className="type-label">{type.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="survey-form-builder-wrapper">
        {!isInitialized ? (
          <div className="survey-form-builder-loading">
            <p>설문 문항을 불러오는 중...</p>
          </div>
        ) : formData.length === 0 ? (
          <div className="survey-form-builder-empty">
            <p>문항이 없습니다.</p>
            {isEditing && (
              <button
                className="btn-header-action"
                onClick={() => setShowAddQuestionModal(true)}
                style={{ marginTop: '16px' }}
              >
                <FontAwesomeIcon icon={faPlus} />
                문항 추가
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="survey-questions-grid">
            {groupedForRender.map((group) => {
              const b = group.block || {};
              const hasItems = group.items && group.items.length > 0;
              if (!hasItems) return null;
              const isIntroBlock = (b.id || "").toLowerCase() === "intro" || b.kind === "intro";
              return (
                <div key={b.id || Math.random().toString(16).slice(2)}>
                  <div className={`survey-block-card ${isIntroBlock ? "survey-block-card--intro" : ""}`}>
                    <div className="survey-block-card__title">{b.title || b.id}</div>
                    {b.ai_comment && <div className="survey-block-card__comment">AI 코멘트: {b.ai_comment}</div>}
                  </div>

                  {group.items.map(({ item, actualIndex }) => {
                    const displayIndex = flatRenderList.findIndex((x) => x.actualIndex === actualIndex);
                    const shownNumber = displayIndex >= 0 ? displayIndex + 1 : actualIndex + 1;
                    return (
                      <div 
                        key={item.id || actualIndex} 
                        className={`survey-question-card ${
                          isEditing ? 'survey-question-card--editable' : ''
                        } ${
                          draggedIndex === actualIndex ? 'dragging' : ''
                        } ${dragOverIndex === actualIndex ? 'drag-over' : ''}`}
                        draggable={isEditing}
                        onDragStart={isEditing ? (e) => handleDragStart(e, actualIndex) : undefined}
                        onDragEnd={isEditing ? handleDragEnd : undefined}
                        onDragOver={isEditing ? (e) => handleDragOver(e, actualIndex) : undefined}
                        onDrop={isEditing ? (e) => handleDrop(e, actualIndex) : undefined}
                        onDragLeave={isEditing ? handleDragLeave : undefined}
                      >
                <div className="question-card-header">
                  <div className="question-number">
                    {isEditing && (
                      <FontAwesomeIcon 
                        icon={faGripVertical} 
                        className="grip-icon" 
                      />
                    )}
                    <span>문항 {shownNumber}</span>
                    <span className="question-type-badge">{getElementTypeLabel(item.element)}</span>
                    {item.required && <span className="question-required-badge">필수</span>}
                  </div>
                  {isEditing && (
                    <div className="question-actions">
                      <button
                        className="btn-move"
                        onClick={() => handleMoveUp(actualIndex)}
                        disabled={!findNeighborWithinSameBlock(actualIndex, -1)}
                        title="위로 이동"
                      >
                        <FontAwesomeIcon icon={faChevronUp} />
                      </button>
                      <button
                        className="btn-move"
                        onClick={() => handleMoveDown(actualIndex)}
                        disabled={!findNeighborWithinSameBlock(actualIndex, +1)}
                        title="아래로 이동"
                      >
                        <FontAwesomeIcon icon={faChevronDown} />
                      </button>
                      <button
                        className="btn-edit"
                        onClick={() => handleEditStart(actualIndex)}
                        title="편집"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(actualIndex)}
                        title="삭제"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="question-card-body">
                  {editingIndex === actualIndex ? (
                    <div className="question-edit-mode">
                      <textarea
                        className="question-edit-input"
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        rows={3}
                        autoFocus
                      />
                      <div className="question-edit-actions">
                        <button
                          className="btn-save-edit"
                          onClick={() => handleEditSave(actualIndex)}
                        >
                          저장
                        </button>
                        <button
                          className="btn-cancel-edit"
                          onClick={handleEditCancel}
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="question-text">
                        {item.text || '(질문 내용 없음)'}
                      </div>
                      {/* 선택지 표시/편집 */}
                      {(item.element === 'RadioButtons' || item.element === 'Checkboxes') && (
                        <div className="question-options">
                          <div className="options-header">
                            <div className="options-label">선택지:</div>
                            {isEditing && editingOptionsIndex !== actualIndex && (
                              <button
                                className="btn-edit-options"
                                onClick={() => handleEditOptionsStart(actualIndex)}
                                title="선택지 편집"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                                편집
                              </button>
                            )}
                          </div>
                          
                          {editingOptionsIndex === actualIndex ? (
                            <div className="options-edit-mode">
                              <div className="options-edit-list">
                                {editingOptions.map((opt, optIdx) => (
                                  <div key={optIdx} className="option-edit-item">
                                    <input
                                      type="text"
                                      className="option-edit-input"
                                      value={opt.text}
                                      onChange={(e) => handleOptionTextChange(optIdx, e.target.value)}
                                      placeholder={`선택지 ${optIdx + 1}`}
                                      autoFocus={optIdx === 0}
                                    />
                                    {editingOptions.length > 1 && (
                                      <button
                                        className="btn-delete-option"
                                        onClick={() => handleDeleteOption(optIdx)}
                                        title="삭제"
                                      >
                                        <FontAwesomeIcon icon={faTimes} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <div className="options-edit-actions">
                                <button
                                  className="btn-add-option"
                                  onClick={handleAddOption}
                                >
                                  <FontAwesomeIcon icon={faPlus} />
                                  선택지 추가
                                </button>
                                <div className="options-save-actions">
                                  <button
                                    className="btn-save-options"
                                    onClick={() => handleEditOptionsSave(actualIndex)}
                                  >
                                    저장
                                  </button>
                                  <button
                                    className="btn-cancel-options"
                                    onClick={handleEditOptionsCancel}
                                  >
                                    취소
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="options-list">
                              {item.options && item.options.length > 0 ? (
                                item.options.map((opt, optIdx) => (
                                  <div key={optIdx} className="option-item">
                                    {typeof opt === 'string' ? opt : opt.text || opt.value}
                                  </div>
                                ))
                              ) : (
                                <div className="options-empty">
                                  선택지가 없습니다. 편집 버튼을 눌러 선택지를 추가하세요.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
                    );
                  })}
                </div>
              );
            })}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

SurveyFormBuilder.displayName = 'SurveyFormBuilder';

export default SurveyFormBuilder;
